<script lang="ts">
	import TextArea from '../atoms/TextArea.svelte'
	import Button from '$lib/atoms/Button.svelte'
	import DatePickerInput from '../atoms/DatePickerInput.svelte'
	import AutocompleteInputField from '../autocomplete/AutocompleteInputField.svelte'
	import Icon from '../atoms/Icon.svelte'
	import closeIcon from '@/icons/icon_close.svg'
	import Notifications from '../organisms/Notifications.svelte'
	import { fetchUtils } from '$utils/fetchUtils'
	import { addNotification } from '$lib/stores'
	import { DefaultNotification, NotificationType } from '$types/notification'
	import { t } from '$i18n/i18n'
	import { inputFieldFormats } from '$utils/formatters'
	import { AutocompleteConfig } from '$types/autocompleteFieldConfig'
	import { DatePickerMode } from '$types/datepicker'
	import dayjs from 'dayjs'
	import {
		buildReminderTimestamp,
		CALL_DIRECTION_OPTIONS,
		CALL_DISPOSITION_OPTIONS,
		ENGAGEMENT_CONFIG,
		type EngagementPayload,
		type HubSpotEngagementType,
		MEETING_DURATION_OPTIONS,
		MEETING_OUTCOME_OPTIONS,
		TASK_PRIORITY_OPTIONS,
		TASK_REMINDER_OPTIONS,
		TASK_TYPE_OPTIONS
	} from './HubSpotEngagement.model'

	interface Props {
		recordId: string | null
		type: HubSpotEngagementType
	}
	let { recordId, type = 'call' }: Props = $props()

	const config = $derived(ENGAGEMENT_CONFIG[type])

	// svelecte config for the meeting duration: value = minutes, label shown; not clearable
	const durationAutocompleteConfig = AutocompleteConfig('value', 'label', false, false, true, false)

	let infoText = $state('')
	let dateTimeUnix: number | null = $state(null)
	let callDisposition = $state('')
	let callDirection = $state('')
	let taskReminder = $state('')
	let taskType = $state('TODO')
	let taskPriority = $state('')
	let meetingOutcome = $state('')
	let meetingDurationMinutes = $state(30)
	let submitting = $state(false)
	let engagementDialog: HTMLDialogElement | undefined = $state()
	// dedicated empty host for the flatpickr popup – must NOT contain Svelte-managed children,
	// otherwise flatpickr's foreign DOM node collides with Svelte's reconciliation and the field
	// eventually disappears on repeated open/close cycles
	let pickerAnchor: HTMLElement | undefined = $state()

	export function openHubspotEngagementModal() {
		infoText = ''
		dateTimeUnix = dayjs().unix()
		callDisposition = ''
		callDirection = ''
		taskReminder = ''
		taskType = 'TODO'
		taskPriority = ''
		meetingOutcome = ''
		meetingDurationMinutes = 30
		engagementDialog?.showModal()
	}

	export function closeHubspotEngagementModal() {
		engagementDialog?.close()
	}

	function showError(message: string) {
		addNotification({
			...DefaultNotification(message, NotificationType.ERROR),
			target: 'hubspotEngagement'
		})
	}

	function buildPayload(): EngagementPayload {
		return {
			infoText,
			contactId: recordId,
			timestamp:
				config.dateTimeLabel && dateTimeUnix ? dayjs.unix(dateTimeUnix).toISOString() : null,
			callDisposition: callDisposition || null,
			callDirection: callDirection || null,
			taskReminder: type === 'task' ? buildReminderTimestamp(taskReminder, dateTimeUnix) : null,
			taskType: type === 'task' ? taskType : null,
			taskPriority: taskPriority || null,
			meetingOutcome: meetingOutcome || null,
			meetingDurationMinutes: type === 'meeting' ? Number(meetingDurationMinutes) : null
		}
	}

	async function sendEngagement() {
		if (config.dateTimeLabel && !dateTimeUnix) {
			showError(`Bitte "${config.dateTimeLabel}" setzen.`)
			return
		}
		submitting = true
		try {
			await fetchUtils.post(config.url, buildPayload(), false)
			addNotification(DefaultNotification(config.successMessage, NotificationType.SUCCESS))
			closeHubspotEngagementModal()
		} catch (error) {
			console.error('HubSpot engagement submission failed', error)
			showError($t('UI.HubSpot.error.general'))
		} finally {
			submitting = false
		}
	}
</script>

<dialog bind:this={engagementDialog} class="hem__dialog">
	<!-- Notifications from the base template are not ideally positioned for this modal, so we include the
Notification component here -->
	<Notifications position="absolute" notificationWidth="80%" top="0" target="hubspotEngagement" />
	<!-- Otherwise <dialog>.showModal() focuses the first focusable element (the date field),
	     which makes flatpickr open immediately. We redirect the autofocus to the card itself. -->
	<div class="hem__card" tabindex="-1" autofocus>
		<div class="hem__header">
			<h1>{config.title}</h1>
			<Icon
				clickable
				on:click={closeHubspotEngagementModal}
				size={24}
				src={closeIcon}
				titleAttr={$t('UI.button.close')}
			/>
		</div>

		<div class="hem__options">
			{#if config.dateTimeLabel}
				<div class="hem__field hem__field--datetime">
					<h2>{config.dateTimeLabel}</h2>
					<!-- empty host for the flatpickr popup (see pickerAnchor comment above).
					     Must come BEFORE the picker and be bound before it mounts, so that
					     appendTo is never null (otherwise a TypeError is thrown in flatpickr). -->
					<div class="hem__picker-host" bind:this={pickerAnchor}></div>
					{#if pickerAnchor}
						<DatePickerInput
							id="hemDate"
							label=""
							bind:date={dateTimeUnix}
							mode={DatePickerMode.DATETIME}
							format={inputFieldFormats.FULL}
							placeholder="Datum & Uhrzeit"
							appendTo={pickerAnchor}
						/>
					{/if}
				</div>
			{/if}

			{#if type === 'call'}
				<div class="hem__field">
					<h2>Anrufergebnis</h2>
					<select bind:value={callDisposition}>
						{#each CALL_DISPOSITION_OPTIONS as option (option.value)}
							<option value={option.value}>{option.label}</option>
						{/each}
					</select>
				</div>
				<div class="hem__field">
					<h2>Anrufrichtung</h2>
					<select bind:value={callDirection}>
						{#each CALL_DIRECTION_OPTIONS as option (option.value)}
							<option value={option.value}>{option.label}</option>
						{/each}
					</select>
				</div>
			{/if}

			{#if type === 'task'}
				<div class="hem__field">
					<h2>Erinnerung senden</h2>
					<select bind:value={taskReminder}>
						{#each TASK_REMINDER_OPTIONS as option (option.value)}
							<option value={option.value}>{option.label}</option>
						{/each}
					</select>
				</div>
				<div class="hem__field">
					<h2>Aufgabentyp</h2>
					<select bind:value={taskType}>
						{#each TASK_TYPE_OPTIONS as option (option.value)}
							<option value={option.value}>{option.label}</option>
						{/each}
					</select>
				</div>
				<div class="hem__field">
					<h2>Priorität</h2>
					<select bind:value={taskPriority}>
						{#each TASK_PRIORITY_OPTIONS as option (option.value)}
							<option value={option.value}>{option.label}</option>
						{/each}
					</select>
				</div>
			{/if}

			{#if type === 'meeting'}
				<div class="hem__field">
					<h2>Dauer</h2>
					<AutocompleteInputField
						id="hemMeetingDuration"
						label=""
						bind:value={meetingDurationMinutes}
						options={MEETING_DURATION_OPTIONS}
						autocompleteConfig={durationAutocompleteConfig}
						hideDropdownArrow={true}
						placeholder="Dauer wählen"
					/>
				</div>
				<div class="hem__field">
					<h2>Meeting-Ergebnis</h2>
					<select bind:value={meetingOutcome}>
						{#each MEETING_OUTCOME_OPTIONS as option (option.value)}
							<option value={option.value}>{option.label}</option>
						{/each}
					</select>
				</div>
			{/if}
		</div>

		<div class="hem__message">
			<h2>Notiz</h2>
			<TextArea
				bind:text={infoText}
				height="9rem"
				labelGap="0"
				marginTop="0"
				maxHeight="12.5rem"
				resize="vertical"
				placeholder={config.placeholder}
			/>
		</div>
		<div class="hem__divider"></div>
		<div class="hem__buttons">
			<Button variant="primary" titleAttr="Senden" disabled={submitting} onclick={sendEngagement}>
				{submitting ? 'Wird gesendet…' : 'Senden'}
			</Button>
		</div>
	</div>
</dialog>

<style lang="scss">
	@use '$styles/global';

	dialog.hem__dialog {
		margin: auto;
		border: none;
		width: 31.25rem;
		padding: 0;
		background: none;
		font-family: unset;
		font-style: unset;
		font-weight: unset;
		font-size: unset;
		line-height: unset;
		color: unset;
	}

	dialog.hem__dialog::backdrop {
		background: rgba(0, 0, 0, 0.8);
	}

	/* flatpickr writes page coordinates as an inline style onto the calendar – inside the top-layer
	   dialog these are interpreted relative to the appendTo container and are therefore wrong. The
	   calendar is attached to the field (appendTo); we anchor it deterministically right below it,
	   left-aligned. This removes flatpickr's (delayed) repositioning entirely. */
	dialog.hem__dialog :global(.flatpickr-calendar) {
		position: absolute !important;
		top: 100% !important;
		left: 0 !important;
		right: auto !important;
		margin-top: 0.25rem;
		transform: none !important;
		/* no fade/slide animation -> popup is there instantly, no jumping afterwards */
		animation: none !important;
		/* flatpickr measures the calendar width (because of weekNumbers) once when the instance is
		   created – at that point the dialog is still closed (display: none) and the measured
		   inline width is 0px. width: auto lets the calendar grow via shrink-to-fit around its
		   fixed-size children. */
		width: auto !important;

		/* the little positioning arrow then points at nothing – hide it */
		&::before,
		&::after {
			display: none;
		}
	}

	/* second inline width measured by flatpickr: the day grid gets
	   (days.offsetWidth + 1) * showMonths = 1px when the instance is built in the closed dialog.
	   Fix it via shrink-to-fit around the fixed-size .dayContainer as well. */
	dialog.hem__dialog :global(.flatpickr-calendar .flatpickr-days) {
		width: auto !important;
	}

	.hem__card {
		@include global.flex-col($row-gap: 1.5rem, $alignment: flex-start);

		width: 100%;
		background: white;
		padding: 2rem;
		border-radius: 0.5rem;
		border: 1px solid #d9d9d9; // TODO: Replace color with variable when styleguide is up-to-date
	}

	.hem__header {
		@include global.flex-row($justify: space-between);

		width: 100%;
	}

	.hem__options {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 1rem;
		width: 100%;

		&:empty {
			display: none;
		}
	}

	.hem__field {
		@include global.flex-col($row-gap: 0.5rem, $alignment: flex-start);

		/* anchor for the absolutely positioned flatpickr popup (appendTo) – must always be set,
		   regardless of whether the date field spans the full row or only occupies a single cell */
		&--datetime {
			width: 100%;
			position: relative;
			/* DatePickerInput does not set --container-width in the non-material branch; without a
			   value its root falls back to width:auto (content width). Force it to 100%. */
			--container-width: 100%;
		}

		/* the empty popup host must not generate its own box, otherwise it would be an extra
		   flex item and contribute a row-gap that pushes the picker down. */
		.hem__picker-host {
			display: contents;
		}

		select {
			width: 100%;
			padding: 0.375rem 0.5rem;
			font: inherit;
			font-size: 0.75rem;
			border: 1px solid #d9d9d9;
			border-radius: 0.125rem;
			background: white;
		}
	}

	.hem__message {
		@include global.flex-col($row-gap: 1rem, $alignment: flex-start);

		width: 100%;
	}

	.hem__divider {
		width: 100%;
		height: 0.0625rem;
		background: #d9d9d9;
	}

	.hem__buttons {
		@include global.flex-row($col-gap: 1rem, $justify: flex-end);

		width: 100%;
		margin-top: -0.5rem;
	}

	@media (max-width: global.$small) {
		.hem__card {
			padding: 1.5rem;
		}

		.hem__options {
			grid-template-columns: 1fr;
		}
	}

	/* FIXME: Remove when we got rid of Sirius/wondergem.css since it adds some weird stylings to our h1 tags...I hate Sirius. */
	h1,
	h2 {
		display: unset;
		width: unset;
		padding: unset;
		border: unset;
		margin: unset;
	}
</style>
